import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { FetchSiteDevicesQuery } from '@/lib/api/sites/devices';
import { useQuery, useMutation } from '@tanstack/react-query';
import { InteractionTabProps, MonitorAlertFullDto } from '../types';
import { Button } from '@/components/ui/button';
import { Zap, Circle } from 'lucide-react';
import { OperateRelayMut } from '@/lib/api/monitoring/relays';
import { toast } from '@/hooks/use-toast';
import { useCreateAlertNote } from '@/hooks/useCreateAlertNote';
import { getVideoProcessorId } from '@/app/Devices/utils/utils';

type RelayDevice = {
  id: string;
  name: string;
};

type RelayStatus = {
  isOn: boolean;
  isOperating: boolean;
};

export const RelayTab: React.FC<InteractionTabProps> = ({ projectId, companyId, useAlertEvents, alertId, videoProcessorId }) => {
  const [relayStatuses, setRelayStatuses] = useState<Record<string, RelayStatus>>({});
  const [activeRelay, setActiveRelay] = useState<string | null>(null);
  const operationInProgressRef = useRef<Set<string>>(new Set());
  
  // Use the same device query that's already being used in AlertHeader
  const { data: siteDevices } = useQuery(
    FetchSiteDevicesQuery({
      projectId: projectId!,
      companyId: companyId!,
    })
  );

  const operateRelayMut = useMutation(OperateRelayMut());

  // Activity logging hook
  const createAlertNote = useCreateAlertNote({
    companyId: companyId!,
    projectId: projectId!,
    alertId: alertId!,
  });

  // Get the relay devices based on the selected event
  const relayDevices = useMemo(() => {
    const relays: RelayDevice[] = [];

    if (!videoProcessorId || !siteDevices?.devices) return relays;

    // Get the camera ID from the selected event
    const eventCameraId = videoProcessorId;
    if (!eventCameraId) return relays;

    // Find the device with the matching secondary device ID using the same logic as AlertHeader
    const eventDevice = siteDevices.devices.find(device => 
      device.secondaryDevice ? getVideoProcessorId(device.secondaryDevice) === eventCameraId : false
    );
    
    if (!eventDevice?.secondaryDevice?.id) return relays;

    const eventSecondaryDeviceId = eventDevice.secondaryDevice.id;

    // Now find all other devices' relays where relatedComponentGroups contains this secondary device ID
    for (const device of siteDevices.devices) {
      const secondaryDevice = device.secondaryDevice;
      if (!secondaryDevice) continue;

      if (secondaryDevice.relays) {
        const deviceRelays = Array.isArray(secondaryDevice.relays) ? secondaryDevice.relays : [secondaryDevice.relays];
        
        deviceRelays.forEach((relay, index: number) => {
          if (relay?.relatedComponentGroups?.includes(eventSecondaryDeviceId)) {
            const relayId = relay.id || `${secondaryDevice.id}_relay_${index}`;
            relays.push({
              id: relayId,
              name: relay.name || `${secondaryDevice.name} - Relay ${index + 1}`,
            });
          }
        });
      }
    }

    return relays;
  }, [videoProcessorId, siteDevices]);

  // Helper to get current relay status
  const getRelayStatus = useCallback((relayId: string): RelayStatus => {
    return relayStatuses[relayId] || { isOn: false, isOperating: false };
  }, [relayStatuses]);

  // Helper to update relay status
  const updateRelayStatus = useCallback((relayId: string, updates: Partial<RelayStatus>) => {
    setRelayStatuses(prev => ({
      ...prev,
      [relayId]: { ...getRelayStatus(relayId), ...updates }
    }));
  }, [getRelayStatus]);

  const operateRelay = useCallback(async (relayId: string, turnOn: boolean) => {
    const operationKey = `${relayId}-${turnOn}`;
    
    // Prevent duplicate operations
    if (operationInProgressRef.current.has(operationKey)) {
      return;
    }
    
    // Find the relay name for logging
    const relayDevice = relayDevices.find(relay => relay.id === relayId);
    const relayName = relayDevice?.name || `Relay ${relayId}`;
    
    try {
      operationInProgressRef.current.add(operationKey);
      
      // Set operating state
      updateRelayStatus(relayId, { isOperating: true });
      
      await operateRelayMut.mutateAsync({
        companyId: companyId!,
        projectId: projectId!,
        relayId,
        turnOn,
      });
      
      // Update status on successful operation
      updateRelayStatus(relayId, { isOn: turnOn, isOperating: false });
      
      // Log the activity
      try {
        await createAlertNote.mutateAsync({
          note: `${relayName} ${turnOn ? 'triggered' : 'deactivated'}`,
          nonActioningNote: true, // This is a system action, not user input
        });
      } catch (logError) {
        console.warn('Failed to log relay activity:', logError);
        // Don't fail the entire operation if logging fails
      }
      
      toast({
        title: `Relay ${turnOn ? 'activated' : 'deactivated'}`,
        description: `Relay operation completed successfully`,
      });
    } catch (error) {
      // Keep the previous on/off state but clear operating state
      updateRelayStatus(relayId, { isOperating: false });
      
      toast({
        title: 'Relay operation failed',
        description: error instanceof Error ? error.message : 'Failed to operate relay',
        variant: 'destructive',
      });
    } finally {
      operationInProgressRef.current.delete(operationKey);
    }
  }, [companyId, projectId, operateRelayMut, updateRelayStatus, relayDevices, createAlertNote]);

  const handleOperatePress = useCallback((relayId: string) => {
    if (activeRelay === relayId) return; // Already active
    
    // Set visual state immediately
    setActiveRelay(relayId);
    
    // Then trigger API call
    setTimeout(() => operateRelay(relayId, true), 0);
  }, [operateRelay, activeRelay]);

  const handleOperateRelease = useCallback((relayId: string) => {
    if (activeRelay !== relayId) return; // Not the active relay
    
    // Clear visual state immediately
    setActiveRelay(null);
    
    // Then trigger API call
    setTimeout(() => operateRelay(relayId, false), 0);
  }, [operateRelay, activeRelay]);

  const handleTurnOffRelay = useCallback((relayId: string) => {
    operateRelay(relayId, false);
  }, [operateRelay]);

  // Global mouse up handler to ensure we don't get stuck in pressed state
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (activeRelay) {
        handleOperateRelease(activeRelay);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [activeRelay, handleOperateRelease]);

  if (!videoProcessorId || (!alertId && useAlertEvents)) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <div className="h-12 w-12 rounded-full bg-gray-700 flex items-center justify-center">
            <Zap className="h-6 w-6 text-gray-400" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Relay Controls</h3>
        {useAlertEvents ? (
          <p className="text-sm text-gray-400 max-w-sm">
            Select an event to enable relay control functionality. Once an event is selected, you'll be able to control connected relays.
          </p>
        ) : (
          <p className="text-sm text-gray-400 max-w-sm">
            Select a device to enable relay control functionality. Once a device is selected, you'll be able to control connected relays.
          </p>
        )}
      </div>
    );
  }

  if (relayDevices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <div className="h-12 w-12 rounded-full bg-gray-700 flex items-center justify-center">
            <Zap className="h-6 w-6 text-gray-400" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">No Relays Available</h3>
        <p className="text-sm text-gray-400 max-w-sm">
          No relays are linked to the currently selected device. Configure relay-to-camera links in the site devices to enable relay controls.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-sm font-semibold text-white mb-2">Relay Controls</h3>
        <p className="text-xs text-gray-400 mb-2">
          Hold to operate relays linked to the selected camera.
        </p>
        <p className="text-xs text-gray-500">
          ({relayDevices.length} relay{relayDevices.length !== 1 ? 's' : ''} available)
        </p>
      </div>
      
      {/* Scrollable relay list */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {relayDevices.map((relay, index) => {
          const status = getRelayStatus(relay.id);
          const isActivelyPressed = activeRelay === relay.id;
          const hotkeyNumber = index + 1; // 1, 2, 3, 4...
          
          return (
            <div
              key={relay.id}
              className="flex flex-col rounded-lg border border-gray-600 bg-gray-800/30 hover:bg-gray-800/50 p-3 transition-colors"
            >
              {/* Relay info row */}
              <div className="flex items-center gap-3 mb-3">
                {hotkeyNumber <= 9 && (
                  <div className="flex items-center justify-center w-6 h-6 bg-gray-700 rounded text-xs font-mono text-gray-300">
                    {hotkeyNumber}
                  </div>
                )}
                <Zap className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm font-medium text-white truncate">{relay.name}</span>
                  <Circle
                    className={`h-2 w-2 flex-shrink-0 ${
                      status.isOn ? 'fill-green-500 text-green-500' : 'fill-gray-500 text-gray-500'
                    }`}
                  />
                </div>
              </div>
              
              {/* Control buttons row */}
              <div className="flex gap-2">
                {status.isOn && !isActivelyPressed && (
                  <Button
                    variant="outline"
                    onClick={() => handleTurnOffRelay(relay.id)}
                    className="flex-1 text-sm px-2 py-2 select-none bg-red-500/20 border-red-500 text-red-300 hover:bg-red-500/30"
                  >
                    Turn Off
                  </Button>
                )}
                <Button
                  variant="outline"
                  disabled={status.isOperating}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOperatePress(relay.id);
                  }}
                  onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOperateRelease(relay.id);
                  }}
                  className={`flex-1 text-sm px-3 py-2 select-none ${
                    isActivelyPressed
                      ? 'bg-yellow-500/30 border-yellow-500 text-yellow-200'
                      : status.isOperating
                      ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300'
                      : 'hover:bg-yellow-500/20 hover:border-yellow-500 hover:text-yellow-300'
                  }`}
                >
                  {isActivelyPressed ? 'Operating' : status.isOperating ? 'Operating...' : 'Operate'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};